'use client';

import React, { useState } from 'react';
import { Plus, UserPlus } from 'lucide-react';
import ClientsTable from './ClientsTable';
import TeamMembersTable from './TeamMembersTable';
import InviteTeamMemberModal from './InviteTeamMemberModal';
import AddClientModal from './AddClientModal';
import RolePermissionMatrix from './RolePermissionMatrix';

export interface Client {
  id: string;
  name: string;
  industry: string;
  connectedPages: number;
  activeCampaigns: number;
  monthlySpend: number;
  health: 'good' | 'warning' | 'critical';
  status: 'active' | 'onboarding' | 'paused';
  since: string;
  manager: string;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'manager' | 'editor';
  assignedClients: number;
  assignedClientNames: string[];
  lastActive: string;
  status: 'active' | 'invited' | 'inactive';
  avatar: string;
}

export const clients: Client[] = [
  { id: 'client-001', name: 'Bravo Coffee', industry: 'Food & Beverage', connectedPages: 2, activeCampaigns: 2, monthlySpend: 6300, health: 'good', status: 'active', since: 'Jan 2025', manager: 'Soo-Jin Park' },
  { id: 'client-002', name: 'Nexus Tech', industry: 'Technology', connectedPages: 2, activeCampaigns: 2, monthlySpend: 8500, health: 'warning', status: 'active', since: 'Mar 2025', manager: 'Marcus Rivera' },
  { id: 'client-003', name: 'Verde Organic', industry: 'Health & Wellness', connectedPages: 1, activeCampaigns: 2, monthlySpend: 5000, health: 'good', status: 'active', since: 'Feb 2025', manager: 'Priya Nair' },
  { id: 'client-004', name: 'Luxe Living', industry: 'Home & Décor', connectedPages: 1, activeCampaigns: 2, monthlySpend: 6000, health: 'good', status: 'active', since: 'Apr 2025', manager: 'Soo-Jin Park' },
  { id: 'client-005', name: 'UrbanWear Co.', industry: 'Apparel & Fashion', connectedPages: 1, activeCampaigns: 2, monthlySpend: 5000, health: 'critical', status: 'active', since: 'May 2025', manager: 'Anika Sharma' },
  { id: 'client-006', name: 'FitTrack Pro', industry: 'Fitness & Sports', connectedPages: 1, activeCampaigns: 1, monthlySpend: 7200, health: 'good', status: 'active', since: 'Jun 2025', manager: 'Daniel Okonkwo' },
  { id: 'client-007', name: 'Solaris Solar', industry: 'Renewable Energy', connectedPages: 1, activeCampaigns: 1, monthlySpend: 3500, health: 'warning', status: 'active', since: 'Jul 2025', manager: 'Priya Nair' },
  { id: 'client-008', name: 'PetPal Supply', industry: 'Pet Products', connectedPages: 1, activeCampaigns: 1, monthlySpend: 2800, health: 'critical', status: 'active', since: 'Aug 2025', manager: 'Anika Sharma' },
  { id: 'client-009', name: 'Meridian Finance', industry: 'Financial Services', connectedPages: 2, activeCampaigns: 0, monthlySpend: 0, health: 'good', status: 'onboarding', since: 'Aug 2026', manager: 'Marcus Rivera' },
];

export const teamMembers: TeamMember[] = [
  { id: 'member-001', name: 'Marcus Rivera', email: 'marcus.rivera@adhive.agency', role: 'admin', assignedClients: 9, assignedClientNames: ['All clients'], lastActive: 'Just now', status: 'active', avatar: 'MR' },
  { id: 'member-002', name: 'Soo-Jin Park', email: 'soo.jin@adhive.agency', role: 'manager', assignedClients: 2, assignedClientNames: ['Bravo Coffee', 'Luxe Living'], lastActive: '14m ago', status: 'active', avatar: 'SP' },
  { id: 'member-003', name: 'Priya Nair', email: 'priya.nair@adhive.agency', role: 'manager', assignedClients: 2, assignedClientNames: ['Verde Organic', 'Solaris Solar'], lastActive: '1h ago', status: 'active', avatar: 'PN' },
  { id: 'member-004', name: 'Anika Sharma', email: 'anika.sharma@adhive.agency', role: 'editor', assignedClients: 2, assignedClientNames: ['UrbanWear Co.', 'PetPal Supply'], lastActive: '3h ago', status: 'active', avatar: 'AS' },
  { id: 'member-005', name: 'Daniel Okonkwo', email: 'daniel.okonkwo@adhive.agency', role: 'editor', assignedClients: 1, assignedClientNames: ['FitTrack Pro'], lastActive: '5h ago', status: 'active', avatar: 'DO' },
  { id: 'member-006', name: 'Lena Hoffmann', email: 'lena.hoffmann@adhive.agency', role: 'editor', assignedClients: 1, assignedClientNames: ['Nexus Tech'], lastActive: 'Yesterday', status: 'active', avatar: 'LH' },
  { id: 'member-007', name: 'Rafael Moreno', email: 'rafael.moreno@adhive.agency', role: 'manager', assignedClients: 1, assignedClientNames: ['Nexus Tech'], lastActive: '2 days ago', status: 'inactive', avatar: 'RM' },
  { id: 'member-008', name: 'Yuki Tanaka', email: 'yuki.tanaka@adhive.agency', role: 'editor', assignedClients: 0, assignedClientNames: [], lastActive: 'Pending', status: 'invited', avatar: 'YT' },
];

export default function ClientTeamContent() {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);

  return (
    <div className="space-y-8 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Clients & Teams</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {clients.length} clients · {teamMembers.filter((m) => m.status === 'active').length} active team members
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowInviteModal(true)} className="btn-secondary text-sm">
            <UserPlus size={14} />
            Invite Member
          </button>
          <button onClick={() => setShowAddClientModal(true)} className="btn-primary text-sm">
            <Plus size={14} />
            Add Client
          </button>
        </div>
      </div>

      {/* Permission Matrix */}
      <RolePermissionMatrix />

      {/* Clients Table */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Client Accounts</h2>
          <p className="text-sm text-muted-foreground">{clients.length} clients</p>
        </div>
        <ClientsTable clients={clients} />
      </div>

      {/* Team Members Table */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Team Members</h2>
          <p className="text-sm text-muted-foreground">{teamMembers.length} members</p>
        </div>
        <TeamMembersTable members={teamMembers} />
      </div>

      <InviteTeamMemberModal open={showInviteModal} onClose={() => setShowInviteModal(false)} clients={clients} />
      <AddClientModal open={showAddClientModal} onClose={() => setShowAddClientModal(false)} />
    </div>
  );
}